import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import {
  ScrollAction,
  scrollActionForTransition,
  seedsScrollForTransition,
} from '../lib/scrollRestoration';
import { INSTANT_SCROLL } from '../lib/scrollBehavior';
import { getActiveScrollContainer, useActiveScrollContainer } from './scrollContainer';
import { markProgrammaticScroll } from '../lib/programmaticScroll';

// App-level scroll restoration for the SPA. Mounted once inside the router
// (see App.js `ScrollRestorationMount`). Owns scroll explicitly because the
// browser can't restore an async-rendered feed — at the moment it would
// restore, the page often isn't tall enough yet (products stream in after
// mount), so the native restore lands short. We take over via
// `history.scrollRestoration = 'manual'`.
//
// Behavior (decision in lib/scrollRestoration.ts):
//   - Fresh navigation (PUSH) / redirect (REPLACE) → top.
//   - Back / forward (POP) → restore the offset remembered for that entry.
//
// Scroller: the WINDOW by default (mobile board + every normal page). A page
// with an inner scroller — the desktop board's FeedScroll div — registers it
// via useRegisterScrollContainer, and we save/restore THAT element instead.
// One remembered offset per history entry, against whatever the active
// scroller was on that entry, so mobile (window) and desktop (container) are
// handled by the same code path.

const KEY_PREFIX = 'thea:scroll:';
// Patience caps on the restore retry loop, both wall-clock so they don't
// depend on refresh rate.
//
// STALL budget: give up once the scroller has gone this long WITHOUT GROWTH,
// not since the retry started — whenever the feed gets taller the clock
// re-arms, so a feed that streams in over several seconds (the shared-board
// callable, a carousel session, the hub's multi-hook first-screen load —
// THE-867) still reaches its restore, while a genuinely-too-short page stops
// being retried soon after it goes quiet. 1s (the original frame-counted
// value) turned out too short for the hub/collection/guide pages: a single
// Firestore round trip between two sections resolving can exceed it on a
// cold load. Widening this helps the page keep trying across that wait, but
// the main reason "Back loses your place" landed short on these pages was
// `scrollToTop`'s missing INSTANT_SCROLL — see its own comment.
//
// HARD budget: an absolute ceiling from the first retry frame, independent
// of growth, so a pathologically slow-but-still-growing page cannot spin
// forever — same shape as useReturnAnchor's APPLY_BUDGET_MS.
const RESTORE_STALL_BUDGET_MS = 4000;
const RESTORE_HARD_BUDGET_MS = 12000;

function savedTopFor(key: string): number | undefined {
  try {
    const raw = window.sessionStorage.getItem(KEY_PREFIX + key);
    if (raw == null) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  } catch {
    return undefined;
  }
}

function writeTop(key: string, top: number): void {
  try {
    window.sessionStorage.setItem(KEY_PREFIX + key, String(top));
  } catch {
    /* sessionStorage unavailable (private mode / quota) — degrade silently */
  }
}

// Read/write/measure against the active scroller — the registered container,
// or the window when none is registered.
function currentTop(container: HTMLElement | null): number {
  return container ? container.scrollTop : window.scrollY;
}
// INSTANT_SCROLL, not the `window.scrollTo(0, top)` two-arg form: this app's
// Bootstrap reboot puts `scroll-behavior: smooth` on `:root` for anyone not
// asking for reduced motion, so an unspecified `behavior` animates — and this
// is exactly the "measure-scroll-remeasure loop" scrollBehavior.ts warns
// against (THE-867 root cause, found live: a restored position would climb
// partway toward its target, get read back by the NEXT frame before the
// animation caught up, trip the "the user scrolled" back-off below, and
// strand the page part-way there). `container.scrollTop =` was never
// affected — only the `scrollTo()` METHOD defers to CSS; direct property
// assignment is already instant.
function scrollToTop(container: HTMLElement | null, top: number): void {
  // Not her — tell the board's engagement-scroll listener this `scroll` event
  // (or run of them, across the restore retry loop below) is code-driven.
  markProgrammaticScroll();
  if (container) container.scrollTop = top;
  else window.scrollTo({ top, left: 0, behavior: INSTANT_SCROLL });
}
function maxTop(container: HTMLElement | null): number {
  return container
    ? container.scrollHeight - container.clientHeight
    : document.documentElement.scrollHeight - window.innerHeight;
}

export function useScrollRestoration(): void {
  const location = useLocation();
  const navType = useNavigationType();
  const container = useActiveScrollContainer();
  // The history key of the page currently on screen — read by the scroll
  // listener (registered once per scroller) so each scroll is filed under the
  // entry the user is actually looking at.
  const keyRef = useRef(location.key);
  keyRef.current = location.key;

  // Take ownership of scroll restoration for the app lifetime; put it back on
  // unmount so we don't leave a global side effect behind in tests.
  useEffect(() => {
    if (typeof window === 'undefined' || !('scrollRestoration' in window.history)) {
      return undefined;
    }
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = prev;
    };
  }, []);

  // Remember the active scroller's offset under its history key, rAF-throttled,
  // so a later POP back to this entry can land where it was left. Re-binds when
  // the active container changes (e.g. the desktop board mounts/unmounts).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const target: HTMLElement | Window = container ?? window;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        writeTop(keyRef.current, currentTop(container));
      });
    };
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      target.removeEventListener('scroll', onScroll as EventListener);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [container]);

  // The scroll action decided for the history entry currently on screen.
  // Computed ONCE when the entry (location.key) changes, then reused when only
  // `container` changes — so a late-registering inner scroller re-applies the
  // SAME decision instead of recomputing it. `null` = touch nothing.
  const prevKeyRef = useRef<string | null>(null);
  const prevPathRef = useRef<string | null>(null);
  const entryActionRef = useRef<ScrollAction | null>(null);

  // On a real route change: restore on back, otherwise reset to the top.
  // Same-page transitions are transparent (see scrollActionForTransition):
  // in-page anchors (#hash) and same-pathname overlays/param toggles — the
  // product-detail sheet's `?pdp=N`, filter params — must leave scroll exactly
  // as-is, so opening/closing a PDP no longer flings the feed. Also re-runs
  // when `container` changes, so a container that registers AFTER the route
  // renders (board mounts → feed registers in an effect) still restores on POP.
  //
  // useLayoutEffect, not useEffect (THE-1140): this runs synchronously after
  // the new route commits but BEFORE the browser paints it. A passive effect
  // only runs after that paint, so its first `requestAnimationFrame` lands
  // one full paint late — whatever the DOM happened to land on (top, or
  // wherever a native/bfcache-style restore froze it) is what the user's eye
  // actually catches, and the correction one frame later reads as a glide.
  // The same rAF scheduled from inside a layout effect still runs once per
  // real frame, but it's the frame that hasn't painted yet, so the restore
  // is already in place for the first paint instead of visibly correcting it.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // A fresh, un-subscribed read of the store rather than the `container`
    // dep (THE-1140 QA follow-up on #868): on the commit where a desktop
    // board's FeedScroll unregisters AND this route's new location commits
    // together (a Back navigation unmounting the board), `container` is
    // whatever useActiveScrollContainer's state was at RENDER time — before
    // this same commit's unregister cleanup (a layout effect, see
    // scrollContainer.ts) has run. Reading the store directly inside this
    // callback, which fires after that cleanup in the same commit, already
    // sees the window (null) instead of the about-to-be-detached board
    // element — closing the gap that let one frame paint at the wrong
    // (stale/default) offset before a later commit corrected it.
    const activeContainer = getActiveScrollContainer();

    if (prevKeyRef.current !== location.key) {
      const samePathname =
        prevPathRef.current !== null && prevPathRef.current === location.pathname;
      const hasHash = location.hash.length > 0;
      prevKeyRef.current = location.key;
      prevPathRef.current = location.pathname;
      entryActionRef.current = scrollActionForTransition({
        navType,
        samePathname,
        hasHash,
        savedTop: savedTopFor(location.key),
      });
      // A transparent overlay entry (the PDP's `?pdp=N` PUSH) inherits the
      // offset it was opened over. Without this, nothing is ever on record for
      // the overlay entry — and a mobile click-out → Back cold reload lands ON
      // that entry, booting the feed at the top with no way back to where the
      // user was (closing the sheet then stranded them at the top of the
      // board). See seedsScrollForTransition for the full story.
      if (seedsScrollForTransition({ navType, samePathname, hasHash })) {
        writeTop(location.key, currentTop(activeContainer));
      }
    }

    const action = entryActionRef.current;
    if (action === null) return undefined;
    if (action.kind === 'reset') {
      scrollToTop(activeContainer, 0);
      return undefined;
    }

    // Restore — but the destination feed may still be streaming in, so the
    // scroller can be too short to reach `top` on the first frame. Re-apply
    // across frames until it's tall enough, re-arming the patience cap on
    // every height increase (see RESTORE_STALL_BUDGET_MS). If anything
    // ELSE moves the scroll mid-restore — the user flicks the feed — back off
    // immediately: their position wins over ours.
    //
    // Timed off the rAF callback's OWN timestamp rather than a fresh
    // `performance.now()` call — same clock in a real browser, but it lets a
    // test drive the budget with a synchronous, controllable fake frame pump
    // instead of needing to mock the global clock.
    const target = action.top;
    let startTime: number | null = null;
    let lastGrowthTime = 0;
    let lastLimit = -1;
    let lastApplied: number | null = null;
    let raf = 0;
    const tryRestore = (ts: number) => {
      if (startTime === null) {
        startTime = ts;
        lastGrowthTime = ts;
      }
      if (
        lastApplied !== null &&
        Math.abs(currentTop(activeContainer) - lastApplied) > 1
      ) {
        return; // user (or something else) scrolled — stop fighting them
      }
      const limit = maxTop(activeContainer);
      lastApplied = Math.min(target, Math.max(0, limit));
      scrollToTop(activeContainer, lastApplied);
      if (limit >= target) return;
      if (limit > lastLimit) lastGrowthTime = ts;
      lastLimit = limit;
      const stalled = ts - lastGrowthTime >= RESTORE_STALL_BUDGET_MS;
      const expired = ts - startTime >= RESTORE_HARD_BUDGET_MS;
      if (!stalled && !expired) {
        raf = window.requestAnimationFrame(tryRestore);
      }
    };
    raf = window.requestAnimationFrame(tryRestore);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
    };
    // location.key is unique per history entry (changes on every navigation,
    // including PUSH to the same path); container re-runs the restore once a
    // late-registering inner scroller appears. navType, location.pathname and
    // location.hash are read fresh here (they change in lockstep with key).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, container]);
}
